import { useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { styles } from '../styles';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { User } from '../types';

const TECHNICIAN_NAMES = ['Vitali Diel', 'Erik Filbert', 'Lisa Meier'];

type RobotStatus = 'ok' | 'fault' | 'unknown';

const CATEGORIES = ['Fertigung', 'Logistik', 'Qualitätssicherung'] as const;
type Category = typeof CATEGORIES[number];

type Robot = {
  id: number;
  name: string;
  category: Category;
  status: RobotStatus;
  technicianIds: number[];
};

const initialRobots: Robot[] = [
  { id: 1, name: 'CNC-Fräse DMG Mori NHX 5000', category: 'Fertigung', status: 'ok', technicianIds: [] },
  { id: 2, name: 'Schweißroboter KUKA KR Cybertech', category: 'Fertigung', status: 'ok', technicianIds: [] },
  { id: 3, name: 'Lackierroboter ABB IRB 5500', category: 'Fertigung', status: 'ok', technicianIds: [] },
  { id: 4, name: 'Spritzgussmaschine Arburg 570 A', category: 'Fertigung', status: 'ok', technicianIds: [] },
  { id: 5, name: 'Drehmaschine Index G220', category: 'Fertigung', status: 'ok', technicianIds: [] },
  { id: 6, name: 'Stanzpresse Schuler MSC 400', category: 'Fertigung', status: 'ok', technicianIds: [] },
  { id: 7, name: 'Laserschneider TruLaser 5030', category: 'Fertigung', status: 'ok', technicianIds: [] },
  { id: 8, name: 'Förderband Interroll DC 24V', category: 'Logistik', status: 'ok', technicianIds: [] },
  { id: 9, name: 'Palettenroboter Fanuc M-410iC', category: 'Logistik', status: 'ok', technicianIds: [] },
  { id: 10, name: 'FTS MiR250', category: 'Logistik', status: 'ok', technicianIds: [] },
  { id: 11, name: 'Hochregalstapler Jungheinrich ETV', category: 'Logistik', status: 'ok', technicianIds: [] },
  { id: 12, name: 'Verpackungsanlage Bosch Pack 403', category: 'Logistik', status: 'ok', technicianIds: [] },
  { id: 13, name: 'Brückenkran Demag KBK', category: 'Logistik', status: 'ok', technicianIds: [] },
  { id: 14, name: 'Kommissionierroboter Swisslog ACPaQ', category: 'Logistik', status: 'ok', technicianIds: [] },
  { id: 15, name: 'Koordinatenmessgerät Zeiss Prismo', category: 'Qualitätssicherung', status: 'ok', technicianIds: [] },
  { id: 16, name: 'Härteprüfer EMCO-TEST DuraVision', category: 'Qualitätssicherung', status: 'ok', technicianIds: [] },
  { id: 17, name: 'Röntgenprüfanlage YXLON FF35 CT', category: 'Qualitätssicherung', status: 'ok', technicianIds: [] },
  { id: 18, name: 'Kamerasystem Cognex In-Sight 9000', category: 'Qualitätssicherung', status: 'ok', technicianIds: [] },
  { id: 19, name: 'Zugprüfmaschine Zwick Roell Z250', category: 'Qualitätssicherung', status: 'ok', technicianIds: [] },
  { id: 20, name: '3D-Scanner GOM ATOS Q', category: 'Qualitätssicherung', status: 'ok', technicianIds: [] },
];

const statusLabel = (s: RobotStatus) =>
  s === 'ok' ? 'Betrieb ok' : s === 'fault' ? 'Störung' : '—';

const statusColor = (s: RobotStatus) =>
  s === 'ok' ? '#4CAF50' : s === 'fault' ? '#E53935' : '#999';

interface RobotsViewProps {
  users: User[];
}

export function RobotsView({ users }: RobotsViewProps) {
  const [robots, setRobots] = useLocalStorage<Robot[]>('pvs_robots', initialRobots);
  const [updatedId, setUpdatedId] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category | 'all'>('all');
  const visibleRobots = activeCategory === 'all'
    ? robots
    : robots.filter(r => r.category === activeCategory);

  const technicianPool = TECHNICIAN_NAMES
    .map(name => users.find(u => u.name === name))
    .filter((u): u is User => !!u);

  const notifyTechnicians = async (robot: Robot, status: RobotStatus) => {
    const recipients = robot.technicianIds
      .map(id => users.find(u => u.id === id))
      .filter((u): u is User => !!u && !!u.mxid);
    if (recipients.length === 0) {
      console.log('Keine Techniker zugewiesen – Nachricht nicht gesendet.');
      return;
    }

    const message =
      status === 'fault'
        ? `Störung gemeldet: Gerät "${robot.name}" benötigt Wartung.`
        : `Betrieb ok: Gerät "${robot.name}" läuft wieder normal.`;

    const el = document.querySelector('tim-messenger') as any;
    if (!el || typeof el.sendDirectMessage !== 'function') {
      console.log('tim-messenger nicht bereit – Nachricht nicht gesendet:', message);
      return;
    }
    for (const r of recipients) {
      try {
        await el.sendDirectMessage({ mxId: r.mxid, message });
      } catch (err) {
        console.log('Fehler beim Senden an', r.mxid, err);
      }
    }
  };

  const setStatus = (id: number, status: RobotStatus) => {
    const robot = robots.find(r => r.id === id);
    setRobots(robots.map(r => (r.id === id ? { ...r, status } : r)));
    setUpdatedId(id);
    setTimeout(() => setUpdatedId(null), 800);
    if (robot) notifyTechnicians({ ...robot, status }, status);
  };

  const toggleTechnician = (robotId: number, techId: number) => {
    setRobots(robots.map(r => {
      if (r.id !== robotId) return r;
      const ids = r.technicianIds || [];
      return {
        ...r,
        technicianIds: ids.includes(techId) ? ids.filter(i => i !== techId) : [...ids, techId],
      };
    }));
  };

  return (
    <ScrollView style={styles.contentContainer}>
      <Text style={styles.contentTitle}>Geräteverwaltung</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12, marginBottom: 8 }}>
        {(['all', ...CATEGORIES] as const).map(cat => {
          const active = activeCategory === cat;
          const label = cat === 'all' ? 'Alle' : cat;
          return (
            <TouchableOpacity
              key={cat}
              onPress={() => setActiveCategory(cat)}
              style={{
                paddingVertical: 6,
                paddingHorizontal: 14,
                borderRadius: 16,
                borderWidth: 1,
                borderColor: active ? 'var(--primary)' : '#ccc',
                backgroundColor: active ? 'var(--primary)' : 'transparent',
              }}
            >
              <Text style={{ color: active ? '#fff' : '#444', fontSize: 13, fontWeight: '600' }}>
                {label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 8 }}>
        {visibleRobots.map(r => (
          <View
            key={r.id}
            style={{
              width: 300,
              padding: 16,
              borderRadius: 12,
              backgroundColor: '#fff',
              borderLeftWidth: 4,
              borderLeftColor: statusColor(r.status),
              shadowColor: '#000',
              shadowOpacity: 0.05,
              shadowRadius: 6,
            }}
          >
            <Text style={{ fontSize: 11, color: '#888', marginBottom: 2, textTransform: 'uppercase' }}>{r.category}</Text>
            <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 4 }}>{r.name}</Text>
            <Text style={{ color: statusColor(r.status), marginBottom: 12 }}>
              {statusLabel(r.status)}
              {updatedId === r.id ? '  ✓' : ''}
            </Text>

            <Text style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Zuständige Techniker</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {technicianPool.map(t => {
                const selected = (r.technicianIds || []).includes(t.id);
                return (
                  <TouchableOpacity
                    key={t.id}
                    onPress={() => toggleTechnician(r.id, t.id)}
                    style={{
                      paddingVertical: 4,
                      paddingHorizontal: 10,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: selected ? 'var(--primary)' : '#ccc',
                      backgroundColor: selected ? 'var(--primary)' : 'transparent',
                    }}
                  >
                    <Text style={{ color: selected ? '#fff' : '#444', fontSize: 12 }}>{t.name}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity
                onPress={() => setStatus(r.id, 'fault')}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: r.status === 'fault' ? '#E53935' : '#fdecea',
                  alignItems: 'center',
                }}
              >
                <Text style={{ color: r.status === 'fault' ? '#fff' : '#E53935', fontWeight: '600' }}>
                  Störung
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setStatus(r.id, 'ok')}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: r.status === 'ok' ? '#4CAF50' : '#e8f5e9',
                  alignItems: 'center',
                }}
              >
                <Text style={{ color: r.status === 'ok' ? '#fff' : '#4CAF50', fontWeight: '600' }}>
                  Betrieb ok
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </View>
    </ScrollView>
  );
}
